import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  getSwiperOptions,
  renderBullet,
  syncInactiveClass,
} from 'js/shared/swiper'
import { initPaginationVisibility, onActiveBulletChange } from 'js/shared/swiper/paginationVisibility'
import { prepareLazyImagePlaceholders, hydrateVisibleSlideImages } from 'js/shared/swiper/lazyImages'

import SwiperNavigation from './SwiperNavigation'
import SlideCounter from './SlideCounter'

import 'js/shared/swiper/adagio-swiper.scss'
import styles from './AdagioSwiper.module.scss'

const cx = classnames.bind(styles)

function syncLazySlideImages(swiper) {
  prepareLazyImagePlaceholders(swiper)
  hydrateVisibleSlideImages(swiper)
}

export default function AdagioSwiper({
  children,
  className,
  slideClassName,
  preset = 'default',
  skipNavigation = false,
  skipPagination = false,
  counter = false,
  onSwiper,
  onSlideChange,
  ...props
}) {
  const [swiperInstance, setSwiperInstance] = useState(null)
  const [prevEl, setPrevEl] = useState(null)
  const [nextEl, setNextEl] = useState(null)
  const [paginationEl, setPaginationEl] = useState(null)

  const { modules, ...presetOptions } = getSwiperOptions({
    preset,
    overrides: props,
    react: true,
  })

  const isAutoWidth = presetOptions.slidesPerView === 'auto'

  // Pagination element becomes available only after mount (callback ref),
  // so (re)initialize the custom bullets visibility once it's bound.
  useEffect(() => {
    if (swiperInstance && !swiperInstance.destroyed && paginationEl) {
      initPaginationVisibility(swiperInstance)
    }
  }, [swiperInstance, paginationEl])

  return (
    <div className={cx('root', className)}>
      <Swiper
        {...presetOptions}
        watchSlidesProgress
        className={isAutoWidth ? 'adagio-swiper-auto' : undefined}
        modules={modules}
        pagination={skipPagination ? false : {
          el: paginationEl,
          clickable: true,
          renderBullet,
          dynamicBullets: false,
        }}
        navigation={skipNavigation ? false : {
          addIcons: false,
          prevEl,
          nextEl,
        }}
        onSwiper={(swiper) => {
          syncInactiveClass(swiper)
          setSwiperInstance(swiper)
          onSwiper?.(swiper)
        }}
        onInit={(swiper) => {
          syncInactiveClass(swiper)
          initPaginationVisibility(swiper)
          syncLazySlideImages(swiper)
        }}
        onResize={(swiper) => {
          syncInactiveClass(swiper)
          initPaginationVisibility(swiper)
          syncLazySlideImages(swiper)
        }}
        onBreakpoint={(swiper) => {
          syncInactiveClass(swiper)
          initPaginationVisibility(swiper)
          syncLazySlideImages(swiper)
        }}
        onLock={syncInactiveClass}
        onUnlock={syncInactiveClass}
        onSlideChange={(swiper) => {
          onActiveBulletChange(swiper)
          syncLazySlideImages(swiper)
          onSlideChange?.(swiper)
        }}
      >
        {children.map((child, index) => (
          <SwiperSlide key={index} className={slideClassName}>
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
      {!skipPagination && (
        <div className={cx('adagio-swiper-pagination')}>
          <div ref={setPaginationEl} className="swiper-pagination"></div>
        </div>
      )}
      {!skipNavigation && (
        <SwiperNavigation prevRef={setPrevEl} nextRef={setNextEl} />
      )}
      {counter && (
        <SlideCounter swiper={swiperInstance} total={children.length} />
      )}
    </div>
  )
}

AdagioSwiper.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
  className: PropTypes.string,
  slideClassName: PropTypes.string,
  preset: PropTypes.string,
  skipNavigation: PropTypes.bool,
  skipPagination: PropTypes.bool,
  counter: PropTypes.bool,
  onSwiper: PropTypes.func,
  onSlideChange: PropTypes.func,
}
